export type MealPlanDateConflictItem = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  status?: string;
};

function getResponsePayload(error: unknown): Record<string, unknown> {
  const responseData = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (responseData && typeof responseData === 'object') {
    return responseData as Record<string, unknown>;
  }

  if (error && typeof error === 'object') {
    return error as Record<string, unknown>;
  }

  return {};
}

export function extractMealPlanDateConflicts(error: unknown): MealPlanDateConflictItem[] {
  const payload = getResponsePayload(error);
  if (payload.code !== 'MEAL_PLAN_DATE_CONFLICT' || !Array.isArray(payload.conflicts)) {
    return [];
  }

  return payload.conflicts
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      return {
        id: String(record.id ?? ''),
        name: String(record.name ?? 'Meal plan'),
        startDate: typeof record.startDate === 'string' ? record.startDate : null,
        endDate: typeof record.endDate === 'string' ? record.endDate : null,
        status: typeof record.status === 'string' ? record.status : undefined,
      };
    })
    .filter((item): item is MealPlanDateConflictItem => Boolean(item?.id));
}

function formatDateRange(item: MealPlanDateConflictItem): string {
  if (item.startDate && item.endDate) {
    return item.startDate === item.endDate
      ? item.startDate
      : `${item.startDate} - ${item.endDate}`;
  }
  return item.startDate || item.endDate || 'unknown dates';
}

export function formatMealPlanDateConflictMessage(conflicts: MealPlanDateConflictItem[]): string {
  if (conflicts.length === 0) {
    return 'A meal plan already exists for the selected date range. Please view the existing meal plan or choose a different date range.';
  }

  const first = conflicts[0];
  const suffix = conflicts.length > 1 ? ` and ${conflicts.length - 1} more` : '';
  return `A meal plan already exists for the selected date range: ${first.name} (${formatDateRange(first)})${suffix}. Please view the existing meal plan or choose a different date range.`;
}
